import "@/index.css";
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useRef } from "react";
import { Toaster, toast } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { api, apiError } from "@/lib/api";
import { Spinner } from "@/components/common";
import { LanguageProvider } from "@/context/i18n";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { GuestRoute } from "@/components/GuestRoute";
import CustomerShell from "@/layouts/CustomerShell";
import AdminShell from "@/layouts/AdminShell";

import Home from "@/pages/Home";
import { Login, Register, LoanLookup, OtpVerification, AdminLogin, ForgotPassword, AdminForgotPassword, ResetPassword } from "@/pages/auth";
import Dashboard from "@/pages/Dashboard";
import MyInsights from "@/pages/MyInsights";
import { Loans, LoanDetail } from "@/pages/Loans";
import { Payment, PaymentSuccess, PaymentFailed } from "@/pages/Payment";
import PayLoan from "@/pages/PayLoan";
import PaymentHistory from "@/pages/Payments";
import CreditCards from "@/pages/CreditCards";
import Wallet from "@/pages/Wallet";import Rewards from "@/pages/Rewards";
import Offers from "@/pages/Offers";
import Refer from "@/pages/Refer";
import Notifications from "@/pages/Notifications";
import Profile from "@/pages/Profile";
import Support from "@/pages/Support";
import AskAI from "@/pages/AskAI";
import CreditScore from "@/pages/CreditScore";
import FeatureGate from "@/components/FeatureGate";
import Games from "@/pages/Games";
import NotFound from "@/pages/NotFound";
import { About, Privacy, Terms, Refund, Faq } from "@/pages/Legal";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminBrandKit from "@/pages/admin/AdminBrandKit";
import CollectionIntelligence from "@/pages/admin/CollectionIntelligence";
import RewardRules from "@/pages/admin/RewardRules";
import ProductionReadiness from "@/pages/admin/ProductionReadiness";
import AdminManagement from "@/pages/admin/AdminManagement";
import AdminAskAIInsights from "@/pages/admin/AdminAskAIInsights";
import AdminCustomerVoice from "@/pages/admin/AdminCustomerVoice";
import { AdminCustomers, AdminCustomerDetail, AdminLoans, AdminLoanSchedule, AdminPayments, AdminRewards, AdminOffers, AdminSupport, AdminReports, AdminAnalytics, AdminAudit, AdminUsers, AdminSettings } from "@/pages/admin/AdminPages";

// Handles the Emergent Google OAuth return (#session_id=...): exchanges it for the app JWT.
function GoogleCallback() {
  const { loginWithToken } = useAuth();
  const nvg = useNavigate();
  const ran = useRef(false);
  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    const sid = new URLSearchParams(window.location.hash.replace(/^#/, "")).get("session_id");
    if (!sid) { nvg("/login", { replace: true }); return; }
    (async () => {
      try {
        const { data } = await api.post("/auth/google", { session_id: sid });
        window.history.replaceState(null, "", "/dashboard");
        loginWithToken(data.token, data.user);
        nvg("/dashboard", { replace: true });
      } catch (e) {
        toast.error(apiError(e));
        nvg("/login", { replace: true });
      }
    })();
  }, [loginWithToken, nvg]);
  return <div className="min-h-screen bg-slate-50"><Spinner label="Signing you in…" /></div>;
}

// Intercept the OAuth callback before ProtectedRoute runs.
function AuthGate({ children }) {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <GoogleCallback />;
  return children;
}

function App() {
  return (
    <AuthProvider>
      <LanguageProvider>
      <BrowserRouter>
        <Toaster position="top-center" richColors />
        <AuthGate>
        <Routes>
          <Route path="/" element={<GuestRoute><Home /></GuestRoute>} />
          <Route path="/login" element={<GuestRoute><Login /></GuestRoute>} />
          <Route path="/register" element={<Register />} />
          <Route path="/loan-lookup" element={<LoanLookup />} />
          <Route path="/otp-verification" element={<OtpVerification />} />
          <Route path="/about" element={<About />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/refund-policy" element={<Refund />} />
          <Route path="/faq" element={<Faq />} />
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/admin/forgot-password" element={<AdminForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />

          <Route element={<ProtectedRoute><CustomerShell /></ProtectedRoute>}>
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/insights" element={<MyInsights />} />
            <Route path="/loans" element={<Loans />} />
            <Route path="/loans/:id" element={<LoanDetail />} />
            <Route path="/payment" element={<PayLoan />} />
            <Route path="/payment/success" element={<PaymentSuccess />} />
            <Route path="/payment/failed" element={<PaymentFailed />} />
            <Route path="/payments" element={<PaymentHistory />} />
            <Route path="/cards" element={<CreditCards />} />
            <Route path="/wallet" element={<Wallet />} />
            <Route path="/rewards" element={<FeatureGate module="rewards"><Rewards /></FeatureGate>} />
            <Route path="/offers" element={<FeatureGate module="offers"><Offers /></FeatureGate>} />
            <Route path="/refer" element={<Refer />} />
            <Route path="/notifications" element={<Notifications />} />
            <Route path="/profile" element={<Profile />} />
            <Route path="/support" element={<FeatureGate module="support"><Support /></FeatureGate>} />
            <Route path="/ask-ai" element={<FeatureGate module="ask_ai"><AskAI /></FeatureGate>} />
            <Route path="/credit-score" element={<FeatureGate flag="credit_score"><CreditScore /></FeatureGate>} />
            <Route path="/games" element={<FeatureGate module="games_all"><Games /></FeatureGate>} />
            <Route path="/link-loan" element={<LoanLookup authed />} />
            <Route path="/link-loan/verify" element={<OtpVerification authed />} />
            <Route path="*" element={<NotFound />} />
          </Route>

          <Route element={<ProtectedRoute admin><AdminShell /></ProtectedRoute>}>
            <Route path="/admin" element={<AdminDashboard />} />
            <Route path="/admin/dashboard" element={<AdminDashboard />} />
            <Route path="/admin/customers" element={<AdminCustomers />} />
            <Route path="/admin/customers/:id" element={<AdminCustomerDetail />} />
            <Route path="/admin/loans" element={<AdminLoans />} />
            <Route path="/admin/loans/:loanId/schedule" element={<AdminLoanSchedule />} />
            <Route path="/admin/payments" element={<AdminPayments />} />
            <Route path="/admin/rewards" element={<AdminRewards />} />
            <Route path="/admin/offers" element={<AdminOffers />} />
            <Route path="/admin/support" element={<AdminSupport />} />
            <Route path="/admin/reports" element={<AdminReports />} />
            <Route path="/admin/analytics" element={<AdminAnalytics />} />
            <Route path="/admin/audit-logs" element={<AdminAudit />} />
            <Route path="/admin/users" element={<AdminUsers />} />
            <Route path="/admin/settings" element={<AdminSettings />} />
            <Route path="/admin/brand-kit" element={<AdminBrandKit />} />
            <Route path="/admin/collection" element={<CollectionIntelligence />} />
            <Route path="/admin/reward-rules" element={<RewardRules />} />
            <Route path="/admin/production-readiness" element={<ProductionReadiness />} />
            <Route path="/admin/admins" element={<AdminManagement />} />
            <Route path="/admin/askai/insights" element={<AdminAskAIInsights />} />
            <Route path="/admin/customer-voice" element={<AdminCustomerVoice />} />
          </Route>
        </Routes>
        </AuthGate>
      </BrowserRouter>
      </LanguageProvider>
    </AuthProvider>
  );
}
export default App;
